import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Lock, Link2, Check } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

/**
 * AtlasBlocksSection — factored "Approfondir" section shared by AppellationDetail,
 * CepageDetail and ProducerDetail. Fetches from `/atlas-blocks/{entityType}/{slug}`
 * (anonymous callers only see `public=true` blocks; logged-in users see everything).
 *
 * Features:
 *  - Category tab bar (emoji + label + counter)
 *  - Markdown rendering (GFM: tables, task lists, strikethrough, autolinks)
 *  - "Enrichi" badge on `public=false` blocks
 *  - Inline images with captions
 *  - Clickable sources (hostname displayed)
 *  - Anchor links `#block-{id}` — deep-linkable, copy-to-clipboard button
 *    Auto-scrolls to the matching block if the URL hash points to a known id.
 */
export default function AtlasBlocksSection({ entityType, slug, emptyLabel, hideEmpty = false }) {
  const [data, setData] = useState(null);
  const [activeCat, setActiveCat] = useState(null);
  const [copied, setCopied] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setData(null);
    setActiveCat(null);
    api.get(`/atlas-blocks/${entityType}/${slug}`)
      .then((r) => {
        if (cancelled) return;
        setData(r.data);
      })
      .catch(() => setData({ count: 0, blocks: [], categories: [], authenticated: false }));
    return () => { cancelled = true; };
  }, [entityType, slug]);

  // Deep-link support — when the URL hash matches a known block id,
  // switch to that block's category and let the browser scroll to it.
  useEffect(() => {
    if (!data || !data.blocks?.length) return;
    const hash = (window.location.hash || "").replace(/^#/, "");
    const match = hash && hash.startsWith("block-") ? hash.slice(6) : null;
    if (!match) {
      // Default: first category with content
      setActiveCat(data.categories?.[0]?.slug || null);
      return;
    }
    const target = data.blocks.find((b) => b.id === match);
    if (target) {
      setActiveCat(target.category);
      // Wait for the tab to render, then scroll.
      setTimeout(() => {
        const el = document.getElementById(`block-${match}`);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 250);
    } else {
      setActiveCat(data.categories?.[0]?.slug || null);
    }
  }, [data]);

  const activeBlocks = useMemo(() => {
    if (!data) return [];
    if (!activeCat) return data.blocks || [];
    return (data.blocks || []).filter((b) => b.category === activeCat);
  }, [data, activeCat]);

  const copyAnchor = async (blockId) => {
    const url = `${window.location.origin}${window.location.pathname}#block-${blockId}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(blockId);
      toast.success("Lien copié");
      setTimeout(() => setCopied((c) => (c === blockId ? null : c)), 1600);
    } catch {
      // Fallback — highlight the URL bar via hash change (still shareable).
      window.location.hash = `block-${blockId}`;
      toast.info("Ancre positionnée dans l'URL");
    }
  };

  if (!data) return null;
  if (data.count === 0) {
    // In embedded contexts (e.g., the Academy Explorer step) the parent
    // already shows the full fiche + a link to the standalone page, so the
    // empty-state placeholder is redundant. Callers pass `hideEmpty` to
    // suppress it and let the section render nothing.
    if (hideEmpty) return null;
    // Otherwise, show a friendly placeholder instead of silently rendering
    // nothing — this used to leave the CourseDetail explorer step empty when
    // the linked appellation had no atlas blocks yet.
    return (
      <div
        className="mt-6 rounded-2xl border border-dashed border-[#1F3A5F]/20 p-6 text-center bg-[#FBF7F3]"
        data-testid="atlas-blocks-empty"
      >
        <div className="text-sm text-[#1F3A5F] font-bold">
          Aucun bloc enrichi disponible pour cette fiche pour l'instant.
        </div>
        <Link
          to={`/appellations/${slug}`}
          target="_blank"
          className="mt-2 inline-flex items-center gap-1 text-xs text-[#E5276A] font-bold hover:underline"
        >
          Ouvrir la fiche{emptyLabel ? ` « ${emptyLabel} »` : ""} dans un nouvel onglet
        </Link>
      </div>
    );
  }
  const cats = data.categories || [];

  return (
    <div className="mt-16" data-testid="atlas-blocks-section">
      <div className="flex items-baseline justify-between gap-4 flex-wrap">
        <div className="text-[11px] uppercase tracking-[0.2em] text-[#E5276A] font-semibold">
          Approfondir
        </div>
        {!data.authenticated && (
          <span className="text-[11px] text-[#8B95A1] inline-flex items-center gap-1">
            <Lock className="w-3 h-3" />
            Contenus enrichis réservés aux membres —{" "}
            <Link to="/login" className="underline hover:text-[#E5276A]">connectez-vous</Link>
          </span>
        )}
      </div>

      {/* Category tab bar */}
      <div className="mt-4 -mx-1 px-1 overflow-x-auto hide-in-pdf" data-testid="atlas-blocks-tabs">
        <div className="flex flex-nowrap gap-2 min-w-max">
          {cats.map((c) => (
            <button
              key={c.slug}
              type="button"
              onClick={() => setActiveCat(c.slug)}
              data-testid={`atlas-blocks-tab-${c.slug}`}
              className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${
                activeCat === c.slug
                  ? "bg-[#1F3A5F] text-white"
                  : "bg-[#FBF7F3] text-[#1F3A5F] hover:bg-[#FCE4ED] hover:text-[#E5276A]"
              }`}
            >
              <span>{c.emoji}</span>
              <span>{c.label}</span>
              <span className="ml-1 text-[10px] opacity-70">{c.count}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="mt-6 space-y-6">
        {activeBlocks.map((b) => (
          <article
            key={b.id}
            id={`block-${b.id}`}
            data-testid={`atlas-block-${b.id}`}
            className={`app-card p-6 md:p-7 relative scroll-mt-24 ${b.public ? "" : "border-l-4 border-[#E5276A]"}`}
          >
            {!b.public && (
              <span className="absolute top-4 right-14 text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-[#FCE4ED] text-[#E5276A] font-bold">
                Enrichi
              </span>
            )}
            <button
              type="button"
              onClick={() => copyAnchor(b.id)}
              data-testid={`atlas-block-anchor-${b.id}`}
              title="Copier le lien vers ce contenu"
              className="absolute top-4 right-4 p-1.5 rounded-lg text-[#8B95A1] hover:text-[#E5276A] hover:bg-[#FCE4ED] transition-colors hide-in-pdf"
            >
              {copied === b.id ? <Check className="w-4 h-4" /> : <Link2 className="w-4 h-4" />}
            </button>

            <h3 className="font-display text-2xl text-[#1F3A5F] leading-tight pr-24">{b.title}</h3>
            <div className="prose prose-sm md:prose-base max-w-none mt-4 text-[#1F3A5F] font-light leading-relaxed">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{b.content || ""}</ReactMarkdown>
            </div>

            {(b.media || []).length > 0 && (
              <div className="mt-4 grid sm:grid-cols-2 gap-3">
                {b.media.map((m, i) => (
                  <figure key={i}>
                    <img
                      src={`${process.env.REACT_APP_BACKEND_URL || ""}${m.url}`}
                      alt={m.caption || b.title}
                      className="w-full rounded-xl border border-[#1F3A5F]/8"
                      loading="lazy"
                    />
                    {m.caption && (
                      <figcaption className="text-xs text-[#8B95A1] italic mt-1.5">{m.caption}</figcaption>
                    )}
                  </figure>
                ))}
              </div>
            )}

            {(b.sources || []).length > 0 && (
              <div className="mt-4 pt-3 border-t border-[#1F3A5F]/8 text-xs text-[#8B95A1]">
                <span className="font-bold">Sources : </span>
                {b.sources.map((s, i) => (
                  <span key={i}>
                    <SourceLink url={s} />
                    {i < b.sources.length - 1 ? " · " : ""}
                  </span>
                ))}
              </div>
            )}
          </article>
        ))}
        {activeBlocks.length === 0 && (
          <div className="text-sm text-[#8B95A1] italic">
            Aucun contenu dans cette catégorie pour l&apos;instant.
          </div>
        )}
      </div>
    </div>
  );
}

function SourceLink({ url }) {
  let host = url;
  try {
    host = new URL(url).hostname;
  } catch {
    // Not a valid URL — render the raw string.
  }
  return (
    <a href={url} target="_blank" rel="noreferrer noopener" className="underline hover:text-[#E5276A]">
      {host}
    </a>
  );
}
